import { buildSrcSet, hasResponsiveVariants } from '../utils/imageUtils'

/**
 * ResponsiveImage
 *
 * Drop-in replacement for <img> that serves WebP variants at the right
 * breakpoint when the image comes from our CDN upload pipeline.
 *
 * For images that already have responsive variants (uploaded via the API),
 * renders a <picture> with a WebP <source srcset> and falls back to the
 * original <img>. For SVGs or external URLs it renders a plain <img>.
 *
 * Props mirror the standard <img> element. Additional props:
 *   - sizes  {string}  The `sizes` attribute (default: "100vw")
 *
 * Usage:
 *   <ResponsiveImage
 *     src={org.logoUrl}
 *     alt="Organization logo"
 *     sizes="(max-width: 768px) 100vw, 400px"
 *     className="org-logo"
 *     loading="lazy"
 *   />
 */
export default function ResponsiveImage({
  src,
  alt = '',
  sizes = '100vw',
  loading = 'lazy',
  className,
  style,
  width,
  height,
  onError,
  ...rest
}) {
  if (!src) return null

  const imgProps = {
    src,
    alt,
    loading,
    className,
    style,
    width,
    height,
    onError,
    ...rest,
  }

  // Plain <img> for SVGs, external URLs, or anything without known variants
  if (!hasResponsiveVariants(src)) {
    return <img {...imgProps} />
  }

  const srcSet = buildSrcSet(src)

  return (
    <picture>
      {/* WebP variants — browser picks the smallest one that fits */}
      <source
        type="image/webp"
        srcSet={srcSet}
        sizes={sizes}
      />
      {/* Fallback: the original URL (already stored as WebP by the upload pipeline) */}
      <img {...imgProps} />
    </picture>
  )
}
